import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ButtonBack, InputField, Layout } from "../../../components"
import { RootState } from "../../app/mainReducer";
import { AcaoHeader, AssinaturaRecebedorField, BotaoFinalizarEntrega, Images, ModalObservacoes, Temporizador } from "../components"
import QuestionarioSatisfacao from "../components/QuestionarioSatisfacao";
import useRegistroEntregaRegras from "../../../hooks/useRegistroEntregaRegras"
import { useEffect, useState } from 'react';
import { Flex } from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { entregaActions } from "../reducer";
import { Carga, CargaItem } from "../../carga/types";
import { buscaCoordenadasParaEntrega } from "../../../utils/geolocalizacao";
import config from "../../../utils/config";
import { resolveQuestionarioSatisfacaoHabilitado } from "../../../utils/questionarioSatisfacaoRegras";
import { RespostaQuestionarioSatisfacaoItem } from "../types/questionario_satisfacao";
import { questionarioSatisfacaoActions } from "../../questionario-satisfacao/reducer";
import { nomeRecebedorTemNomeESobrenome } from "../utils/nomeRecebedor";

const RegistrarEntrega = () => {
  const gestao_entrega_temporario = useSelector((state: RootState) => state.entrega.gestao_entrega_temporario);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { ukey, tipo, j10_ukey } = useParams();
  const e_transbordo = tipo === 'transbordo';
  const carga = useSelector((state: RootState) =>
    (state.carga.carga || state.carga.cargas?.find((i) => i.j14_ukey === ukey))
  );
  const nota = useSelector((state: RootState) => carga?.itens?.find((i: CargaItem) => i.j10_ukey === j10_ukey));
  const e_retira = !e_transbordo && carga?.e_retira == '1';

  const cpfMotorista = useSelector((state: RootState) => state.login.parceiro?.cpf_cnpj);
  const pesquisaQuestionario = useSelector((state: RootState) => state.questionarioSatisfacao?.pesquisa);
  const clientesQueResponderam = useSelector(
    (state: RootState) => state.questionarioSatisfacao?.clientesQueResponderamQuestionario ?? []
  );
  const [respostasQuestionario, setRespostasQuestionario] = useState<RespostaQuestionarioSatisfacaoItem[]>([]);

  const [rotaDirecionar, setRotaDirecionar] = useState<string | null>(null);

  useEffect(() => {
    if (nota?.nota_dados_complementares_entrega_link_app) {
      const parsedUrl = new URL(nota?.nota_dados_complementares_entrega_link_app);
      setRotaDirecionar(parsedUrl.pathname);
    } else {
      setRotaDirecionar(null);
    }
  }, [])

  useEffect(() => {
    if (!gestao_entrega_temporario) {
      if (e_transbordo) {
        navigate(`/`)
        return;
      }

      if (rotaDirecionar) {
        navigate(rotaDirecionar);
        return;
      }

      if (config.agrupaCargasPeloAgrupamento && carga?.n01_ukey) {
        navigate(`/agrupamentos/${carga?.n01_ukey}`);
        return;  
      }

      navigate(`/cargas/${ukey}`)
    }
  }, [navigate, gestao_entrega_temporario]);

  const [openModalObservacoes, setOpenModalObservacoes] = useState(false);
  const [openedModalObservacoes, setOpenedModalObservacoes] = useState(false);
  const regrasEntrega = useRegistroEntregaRegras();

  const a03Cliente = nota?.cliente_ukey as string | undefined;
  const clienteJaRespondeuLocal = !!a03Cliente && clientesQueResponderam.includes(a03Cliente);

  const clienteJaRespondeuApi = nota?.questionario_ja_respondido == 1;

  const mostrarQuestionario =
    resolveQuestionarioSatisfacaoHabilitado(cpfMotorista) &&
    !!pesquisaQuestionario &&
    !clienteJaRespondeuApi &&
    !clienteJaRespondeuLocal;

  const questionarioCompleto = () => {
    if (!mostrarQuestionario || !pesquisaQuestionario) return true;
    const obrigatorias = (pesquisaQuestionario.perguntas ?? []).filter(
      (p) => p.tipo_resposta == "estrelas" && (p.obrigatoria ?? 1) == 1
    );
    return obrigatorias.every((p) =>
      respostasQuestionario.some((r) => r.r11_ukey == p.ukey && !!r.r12_ukey)
    );
  };

  return (
    <Layout showFooter={true} isLoading={false} title={
      <>
        <ButtonBack />
        <Temporizador />
      </>
    }>
      <AcaoHeader label="Registrar Entrega" />

      <Flex px={4} width="full" direction="column">
        {gestao_entrega_temporario && <Formik
          enableReinitialize
          initialValues={gestao_entrega_temporario}
          onSubmit={async (val, { setErrors }) => {
            if (mostrarQuestionario && !questionarioCompleto()) {
              dispatch(entregaActions.setError("Responda todas as perguntas de avaliação com estrelas."));
              return;
            }
            if (regrasEntrega.nomeRecebedorObrigatorio) {
              const nome = String(val.nome_recebedor || "").trim();
              if (!nome) {
                setErrors({ nome_recebedor: "Informe o nome do recebedor." });
                return;
              }
              if (!nomeRecebedorTemNomeESobrenome(nome)) {
                setErrors({
                  nome_recebedor: "Informe nome e sobrenome do recebedor (nome completo).",
                });
                return;
              }
            }
            if (regrasEntrega.fotoCanhotoObrigatoria && !val.url_imagem1) {
              setErrors({ url_imagem1: "Tire a foto do canhoto!" })
              return;
            }
            if (regrasEntrega.assinaturaRecebedorObrigatoria && !String(val.url_assinatura_recebedor || "").trim()) {
              setErrors({ url_assinatura_recebedor: "Colete a assinatura do recebedor." })
              return;
            }

            if (!openedModalObservacoes && !e_retira) {
              setOpenModalObservacoes(true);
              return;
            }

            if (val?.teve_devolucao_mercadoria === null && !e_retira && !e_transbordo && nota?.e_nota_pallet != '1') {
              setErrors({ teve_devolucao_mercadoria: 'Selecione uma opção' })
              return;
            }

            if (val?.teve_devolucao_mercadoria == 1 && !e_retira && !e_transbordo && nota?.e_nota_pallet != '1') {
              if (!val?.observacoes) {
                setErrors({ observacoes: 'Preencha o motivo da devolução.' })
                return;
              }

              if (!val?.url_imagem2) {
                setErrors({ url_imagem2: 'Tire a foto da Nota Fiscal' })
                return;
              }
            }

            if (!e_retira && !e_transbordo && nota?.e_nota_pallet == '1') {
                if (
                    val.status_nota_pallet_gestao_entrega == 1 &&
                    ! val.motivo_nao_retorno_do_vale_pallet
                ) {
                    setErrors({
                        motivo_nao_retorno_do_vale_pallet: 'Explique por que não vai ter o retorno'
                    })
                    return;
                }

                if (
                    val.status_nota_pallet_gestao_entrega == 3 &&
                    ! val.url_imagem_vale_pallet
                ) {
                    setErrors({
                        url_imagem_vale_pallet: 'Adicione a foto do vale pallet'
                    })
                    return;
                }
            }

            const coordenadas = e_retira ? { latitude: 0, longitude: 0 } : await buscaCoordenadasParaEntrega(val);

            if (!coordenadas) {
              dispatch(entregaActions.setError("Não foi possível obter a geolocalização. Verifique se o GPS está ativo, conceda permissão ao navegador e tente novamente ao ar livre."));
              return;
            }

            if (mostrarQuestionario && a03Cliente) {
              dispatch(questionarioSatisfacaoActions.marcaClienteRespondeuQuestionario(a03Cliente));
            }

            dispatch(entregaActions.registraNovoGestaoEntrega({
              gestao: {
                ...val,
                teve_devolucao_mercadoria: e_retira ? 0 : val.teve_devolucao_mercadoria,
                latitude: coordenadas.latitude,
                longitude: coordenadas.longitude,
                respostas_questionario: mostrarQuestionario ? respostasQuestionario : [],
              },
              carga: carga as Carga
            }))
          }}
        >
          {({ values, handleSubmit, isSubmitting }) => (
            <Form>
              <InputField
                label="Nome do Recebedor"
                placeholder={
                  regrasEntrega.nomeRecebedorObrigatorio
                    ? "Nome e sobrenome do recebedor"
                    : "Nome do Recebedor"
                }
                name="nome_recebedor"
              />

              <Images large={true} />

              {mostrarQuestionario && (
                <QuestionarioSatisfacao onChange={setRespostasQuestionario} />
              )}

              {regrasEntrega.assinaturaRecebedorObrigatoria && <AssinaturaRecebedorField />}

              {!e_retira && <ModalObservacoes values={values} notaPallet={!e_transbordo && nota?.e_nota_pallet == '1'} onSubmited={handleSubmit} onOpenModal={openModalObservacoes} onOpenedModal={() => setOpenedModalObservacoes(true)} isLoading={isSubmitting} />}

              <BotaoFinalizarEntrega isLoading={isSubmitting} />
            </Form>
          )}
        </Formik>
        }
      </Flex>
    </Layout>
  )
}

export default RegistrarEntrega;
